<template>
  <div class="menu--item" :data-menu-item-id="item.mi_id" v-if="isScheduled || $store.state.isAdminView"
       :class="{'menu--item-highlighted': (item.mi_highlight === 1)}" :id="'menu--item-' + item.mi_id">
    <h3 class="menu--item_title">
      <span v-if="steakSize.length" class="menu--item_size" v-html="steakSize"></span>
      <span v-html="title"></span>
      <span v-if="item.mi_description.length" class="menu--item_description" v-html="item.mi_description"></span>
    </h3>

    <menu-schedules v-if="$store.state.isAdminView && item.normalizedSchedules.length" :normalized-schedules="item.normalizedSchedules"></menu-schedules>
    <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="visual"></menu-tag>

    <menu-item-price v-if="item.price_display.length" :price-to-display="item.price_display" :price-label="item.price_label"></menu-item-price>
  </div>
</template>

<script lang="ts">
/**
 * Custom component for WV Steak House steak items.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-08-29 (1.0.0) (jhoward)
 *      - <biminiSteakItem> copied to <WVSteakHouseSteakItem>.
 *
 *    2024-07-19 (1.0.1) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <wv-steak-house-item>.
 *
 *  2025-02-10 (1.1.0) (jhoward)
 *    - Add <menu-schedules> to the <WVSteakHouseSteakItem> component if in admin view.
 *
 *    2025-04-08 (1.2.0) (jhoward)
 *      - Remove "visual_" prefix from class names in <WVSteakHouseSteakItem>.
 *      - Use <menu-item-price> in <WVSteakHouseSteakItem>.
 */

import MenuTag from "../menu-tag.vue";
import {defineComponent, PropType} from "vue";
import {MenuItem} from "../defs";
import {computedIsScheduled} from "../../../helpers/menu-sections";
import MenuSchedules from "../schedule/MenuSchedules.vue";
import MenuItemPrice from "./MenuItemPrice.vue";

export default defineComponent({
  name: 'WVSteakHouseSteakItem',
  components: {
    MenuItemPrice,
    MenuSchedules,
    MenuTag
  },
  props: {
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
  },
  data() {
    return {
      titleRegex: /^\s*(\d+\s?oz)\s(.+)$/
    };
  },
  computed: {
    steakSize(): string {
      const match = this.titleRegex.exec(this.item.mi_title);
      if (match) {
        return match[1];
      }

      return '';
    },
    title(): string {
      const match = this.titleRegex.exec(this.item.mi_title);
      if (match) {
        return match[2];
      }

      return this.item.mi_title;
    },
    isScheduled: computedIsScheduled,
  }
})
</script>

<style scoped>
.menu--item {
  display: grid;
  grid-template-columns: 1fr 0.3fr;
  grid-gap: 0.25em;
  font-size: 1.05em;
}

.menu--item {
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu--item.menu--item-search_highlighted {
  background-color: #f7f3eb !important;
}

.menu--item_title {
  position: relative;
  font-size: 1em;
  font-weight: bold;
  display: grid;
}

.menu--item_description {
  font-weight: 300;
  align-self: center;
}

.menu--tag {
  position: absolute;
  right: 20%;
  top: 1.2em;
  width: 3.5em;
  height: 3.5em;
}

.menu--tag.tag-prime {
  width: 6em;
  height: 6em;
  right: calc(20% - 1.2em);
}

.menu--item-highlighted {
  background-color: #f7f3eb;
}

.menu--item:deep(.menu--item_price) {
  text-align: right;
}

.menu--item_size {
  text-align: right;
}
</style>